"use client";

/**
 * OrderedBeforeBadge — PDP client island for the "ordered before" callout (P21 B3).
 *
 * Renders when a variant has `ordered_before` data from the server. Shows:
 *   "You ordered this before — last: {last_qty} on {date}"
 * and a quick re-add button that adds `last_qty` to the cart via
 * `useCart().addItem` — the last-ordered qty is already checkout-valid
 * (it passed MOQ + increment validation when the original order was placed).
 *
 * Returns null when `orderedBefore` is null/undefined — server passes null
 * for variants the customer never ordered (or for anon/pending sessions where
 * the field is absent). This keeps the check inside the component so the
 * parent (`VariantBlock`) can unconditionally render this island.
 *
 * Pattern mirrors `VariantPriceAndQty` — a thin client island dropped into
 * the server-component PDP page.tsx `VariantBlock`.
 */

import { useState } from "react";
import { useLocale } from "next-intl";
import { Button, useToast } from "@wmart/ui";
import { formatDate } from "@/lib/format";
import type { OrderedBeforeDTO } from "@wmart/shared-types";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";

interface Props {
  variantId: string;
  productTitle: string;
  orderedBefore: OrderedBeforeDTO | null | undefined;
}

export function OrderedBeforeBadge({ variantId, productTitle, orderedBefore }: Props) {
  const { addItem } = useCart();
  const toast = useToast();
  const locale = useLocale();
  const [busy, setBusy] = useState(false);

  if (!orderedBefore) return null;

  async function handleReAdd() {
    if (!orderedBefore) return;
    setBusy(true);
    try {
      await addItem({ variantId, quantity: orderedBefore.last_qty });
      toast.push({
        tone: "success",
        title: "Added to cart",
        body: `${orderedBefore.last_qty} × ${productTitle}`,
      });
    } catch (err) {
      toast.push({
        tone: "danger",
        title: "Could not add to cart",
        body: err instanceof Error ? err.message : "Try again",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-sm border border-accent/30 bg-accent-soft px-4 py-3">
      <p className="text-body-sm text-ink-700">
        <span className="font-medium text-ink-900">You ordered this before</span>
        {" — "}last:{" "}
        <span className="font-medium text-ink-900">{orderedBefore.last_qty}</span>
        {" "}on{" "}
        <span className="font-medium text-ink-900">
          {formatDate(orderedBefore.last_ordered_at, locale)}
        </span>
      </p>
      <Button
        variant="secondary"
        size="sm"
        icon="cart"
        loading={busy}
        disabled={busy}
        onClick={handleReAdd}
      >
        Add to cart
      </Button>
    </div>
  );
}
